import { tipAttrs } from "../../tooltip";
import { useEffect, useState } from "react";
import { ENCOUNTER_ENVIRONMENTS, ENCOUNTER_GROUNDS, encounterGround, MAX_GRID_DIM, MIN_GRID_DIM, encounterShowEnemyHp, type EncounterCommand, type EncounterEnvironment, type EncounterGround, type EncounterState, type GridExtent } from "@lancer-vtt/domain";
import { setHpDisclosureCommand } from "../disclosure";

/** Clamp an integer grid dimension into the reducer's accepted range. */
function clampDim(n: number): number {
  if (!Number.isFinite(n)) return MIN_GRID_DIM;
  return Math.max(MIN_GRID_DIM, Math.min(MAX_GRID_DIM, Math.round(n)));
}

/**
 * **Map setup** (`decisions/0191` Ruling 5, `board-tool-rail` Phase 6): the map's encounter-level
 * settings — Board size, Environment, Ground, and the enemy-HP Disclosure policy. Opened from the GM's
 * room-title menu, in EDIT or PLAY. It replaced the TERRAIN window, whose authoring tools moved to the
 * board tool rail's BUILD section (Phase 5). GM-gated by the `disabled` thread; the server is the wall.
 */
export function MapSetupBody({
  extent,
  state,
  actorId,
  disabled,
  dispatch,
}: {
  extent: GridExtent;
  /** The live encounter state — the current environment, ground and HP disclosure. */
  state: EncounterState;
  actorId: string;
  disabled: boolean;
  dispatch: (command: EncounterCommand) => unknown;
}) {
  const showEnemyHp = encounterShowEnemyHp(state);
  return (
    <div className="gms-content cw-terrain">
      <BoardSection extent={extent} actorId={actorId} disabled={disabled} dispatch={dispatch} />
      <EnvironmentSection environment={state.environment} actorId={actorId} disabled={disabled} dispatch={dispatch} />
      <GroundSection ground={encounterGround(state)} actorId={actorId} disabled={disabled} dispatch={dispatch} />

      {/* Live enemy-HP policy — the
          encounter-level GM opt-in that restores a fully-disclosed hostile's live current-HP bar
          for players (default OFF, RAW-faithful). Pinned here alongside `BoardSection` as this
          window's other encounter-level (not per-unit) GM setting. Issues `set_hp_disclosure`
          (GM-only, undoable) — never a per-combatant control (this is table-wide policy). */}
      <section className="cw-tool-section">
        <div className="cw-section-name">Disclosure</div>
        <div className="cw-subtoggle" role="group" aria-label="show enemy hp">
          <button
            type="button"
            className={`cw-chip${showEnemyHp ? " cw-chip-on" : ""}`}
            disabled={disabled}
            aria-pressed={showEnemyHp}
            title="Stream a fully-disclosed (Scanned) hostile's live current-HP to players"
            onClick={() => !showEnemyHp && dispatch(setHpDisclosureCommand({ actorId, showEnemyHp: true }))}
          >
            SHOW ENEMY HP
          </button>
          <button
            type="button"
            className={`cw-chip${!showEnemyHp ? " cw-chip-on" : ""}`}
            disabled={disabled}
            aria-pressed={!showEnemyHp}
            title="Hide a hostile's live current-HP from players (RAW-faithful snapshot default)"
            onClick={() => showEnemyHp && dispatch(setHpDisclosureCommand({ actorId, showEnemyHp: false }))}
          >
            HIDE
          </button>
        </div>
        <div className="cw-hint">Frozen base stats a Scan reveals are unaffected — this gates only the live HP number.</div>
      </section>
    </div>
  );
}

/** Map setup's **Board** section — the grid `width`×`height` editor (`resize_grid`). */
function BoardSection({
  extent,
  actorId,
  disabled,
  dispatch,
}: {
  extent: GridExtent;
  actorId: string;
  disabled: boolean;
  dispatch: (command: EncounterCommand) => unknown;
}) {
  // Local drafts so typing doesn't fire a command per keystroke (each resize is one undo entry).
  const [w, setW] = useState(String(extent.width));
  const [h, setH] = useState(String(extent.height));
  useEffect(() => {
    setW(String(extent.width));
    setH(String(extent.height));
  }, [extent.width, extent.height]);

  const resize = (width: number, height: number) => {
    const nw = clampDim(width);
    const nh = clampDim(height);
    if (nw === extent.width && nh === extent.height) return; // no-op: don't churn the undo stack
    dispatch({ type: "resize_grid", actorId, width: nw, height: nh });
  };
  const commitW = () => {
    const n = Number(w);
    if (!Number.isFinite(n)) return setW(String(extent.width));
    resize(n, extent.height);
  };
  const commitH = () => {
    const n = Number(h);
    if (!Number.isFinite(n)) return setH(String(extent.height));
    resize(extent.width, n);
  };

  return (
    <section className="cw-tool-section">
      <div className="cw-section-name">Board</div>
      <div className="cw-dims">
        <DimField label="W" value={w} disabled={disabled} onChange={setW} onCommit={commitW} onStep={(d) => resize(extent.width + d, extent.height)} />
        <DimField label="H" value={h} disabled={disabled} onChange={setH} onCommit={commitH} onStep={(d) => resize(extent.width, extent.height + d)} />
      </div>
      <div className="cw-hint">cells ({MIN_GRID_DIM}–{MAX_GRID_DIM})</div>
    </section>
  );
}

/**
 * Map setup's **Environment** section — the GM-facing `set_environment` control (the
 * effects DSL's `scene_environment` predicate is dead without a way to set the fact it reads). A closed
 * None/Zero-G/Underwater picker: "None" dispatches `{ environment: null }` (clears), any other
 * option dispatches `{ environment: <value> }`. A no-op click (re-picking the already-set value)
 * is skipped, mirroring `BoardSection`'s "don't churn the undo stack" discipline.
 */
function EnvironmentSection({
  environment,
  actorId,
  disabled,
  dispatch,
}: {
  environment: EncounterEnvironment | undefined;
  actorId: string;
  disabled: boolean;
  dispatch: (command: EncounterCommand) => unknown;
}) {
  const current = environment ?? null;
  const pick = (next: EncounterEnvironment | null) => {
    if (next === current) return; // no-op: don't churn the undo stack
    dispatch({ type: "set_environment", actorId, environment: next });
  };
  return (
    <section className="cw-tool-section">
      <div className="cw-section-name">Environment</div>
      <div className="cw-dims">
        <button
          type="button"
          className={`cw-chip${current === null ? " cw-chip-on" : ""}`}
          disabled={disabled}
          aria-pressed={current === null}
          onClick={() => pick(null)}
        >
          None
        </button>
        {ENCOUNTER_ENVIRONMENTS.map((e) => (
          <button
            key={e.value}
            type="button"
            className={`cw-chip${current === e.value ? " cw-chip-on" : ""}`}
            disabled={disabled}
            aria-pressed={current === e.value}
            onClick={() => pick(e.value)}
          >
            {e.label}
          </button>
        ))}
      </div>
      <div className="cw-hint">scene-wide hazard tag — content like the EVA Module reads this.</div>
    </section>
  );
}

/**
 * Map setup's **Ground** section — the `set_ground` picker (`terrain-tile-art` ruling 8):
 * the surface the board paints under every cell with no tile. Re-picking the current ground is a
 * no-op, so the undo stack doesn't churn.
 */
function GroundSection({
  ground,
  actorId,
  disabled,
  dispatch,
}: {
  ground: EncounterGround;
  actorId: string;
  disabled: boolean;
  dispatch: (command: EncounterCommand) => unknown;
}) {
  const pick = (next: EncounterGround) => {
    if (next === ground) return;
    dispatch({ type: "set_ground", actorId, ground: next });
  };
  return (
    <section className="cw-tool-section" aria-label="map ground">
      <div className="cw-section-name">Ground</div>
      <div className="cw-dims">
        {ENCOUNTER_GROUNDS.map((g) => (
          <button
            key={g.value}
            type="button"
            className={`cw-chip${ground === g.value ? " cw-chip-on" : ""}`}
            data-ground={g.value}
            disabled={disabled}
            aria-pressed={ground === g.value}
            onClick={() => pick(g.value)}
          >
            {g.label}
          </button>
        ))}
      </div>
      <div className="cw-hint">the surface under every cell with no tile — art only.</div>
    </section>
  );
}

/** One labelled grid-dimension field with −/+ steppers (the Board section's W / H). */
function DimField({
  label,
  value,
  disabled,
  onChange,
  onCommit,
  onStep,
}: {
  label: string;
  value: string;
  disabled: boolean;
  onChange: (v: string) => void;
  onCommit: () => void;
  onStep: (delta: number) => void;
}) {
  return (
    <div className="cw-dim">
      <span className="cw-dim-label">{label}</span>
      <button type="button" className="cw-step" disabled={disabled} aria-label={`decrease ${label}`} {...tipAttrs({ label: `${label} −1` })} onClick={() => onStep(-1)}>
        −
      </button>
      <input
        className="cw-dim-input"
        type="number"
        inputMode="numeric"
        min={MIN_GRID_DIM}
        max={MAX_GRID_DIM}
        value={value}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value)}
        onBlur={onCommit}
        onKeyDown={(e) => {
          if (e.key === "Enter") e.currentTarget.blur();
        }}
      />
      <button type="button" className="cw-step" disabled={disabled} aria-label={`increase ${label}`} {...tipAttrs({ label: `${label} +1` })} onClick={() => onStep(1)}>
        +
      </button>
    </div>
  );
}
